import { useTranslation } from 'react-i18next';

import { Badge } from '@/shared/components/ui/Badge/Badge';

import { type StaffBadgeProps } from './StaffBadge.types';

/**
 * Names a staff account as one, wherever an account is drawn (P27c1).
 *
 * A staff account never appears in a member population — no picker, list,
 * pool or audience draws one — but it can legitimately reach a queue as a
 * reporter, an actor on an audit row, or the subject of a trust match. There
 * it must be named as what it is, or the row reads as a member. Nothing for a
 * member: the badge marks the exception, and a "Member" chip on every row
 * would say nothing.
 *
 * The hint is `title`, not a tooltip control: this is a label, not a place
 * information lives, and the sentence it carries is the same sentence the
 * account's own detail says at length.
 */
export function StaffBadge({ isStaff, className }: StaffBadgeProps) {
  const { t } = useTranslation('uiKit');

  if (!isStaff) {
    return null;
  }

  return (
    <Badge
      tone="neutral"
      variant="outline"
      className={className}
      title={t('account.staffHint')}
      data-account-kind="staff"
    >
      {t('account.staff')}
    </Badge>
  );
}
